import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ProjectCard from './components/ProjectCard';
import FilterBar from './components/FilterBar';
import TechStackVisualizer from './components/TechStackVisualizer';
import ClientTestimonials from './components/ClientTestimonials';
import ProjectStats from './components/ProjectStats';
import { useNavigate } from 'react-router-dom';
import CalendlyWidget from 'components/CalendlyWidget';

const Portfolio = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedTechnology, setSelectedTechnology] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [sortBy, setSortBy] = useState('recent'); // recent, rating, name
  const navigate = useNavigate();

  // Mock project data with stats
  const projectStats = {
    totalProjects: 50,
    completedProjects: 40,
    activeProjects: 5,
    clientSatisfaction: 95,
    technologiesUsed: 25,
    yearOfExperience: 7
  };

  const projects = [
    {
      id: 1,
      title: "E-Commerce Platform Revolution",
      description: `A comprehensive e-commerce solution built with React and Node.js, featuring real-time inventory management, advanced analytics dashboard, and seamless payment integration.\n\nThe platform handles over 10,000 daily transactions and supports multi-vendor marketplace functionality.`,
      category: "Web Application",
      technologies: ["React", "Node.js", "MongoDB", "Stripe", "AWS", "Redis"],
      image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop",
      duration: "6 months",
      teamSize: "8 developers",
      rating: 5,
      status: "Live",
      liveUrl: "https://example-ecommerce.com",
      completedDate: "2024-09-15"
    },
    {
      id: 2,
      title: "Healthcare Management System",
      description: `Advanced healthcare management platform with patient records, appointment scheduling, telemedicine integration, and HIPAA-compliant data handling.\n\nServing 50+ healthcare providers with 99.9% uptime reliability.`,
      category: "Enterprise Software",
      technologies: ["Vue.js", "Python", "PostgreSQL", "Docker", "Azure"],
      image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=800&h=600&fit=crop",
      duration: "8 months",
      teamSize: "12 developers",
      rating: 5,
      status: "Live",
      liveUrl: "https://example-healthcare.com",
      completedDate: "2024-08-20"
    },
    {
      id: 3,
      title: "FinTech Mobile Banking App",
      description: `Secure mobile banking application with biometric authentication, real-time transactions, investment tracking, and AI-powered financial insights.\n\nProcessing $2M+ in daily transactions with bank-grade security.`,
      category: "Mobile Application",
      technologies: ["React Native", "Node.js", "MongoDB", "Firebase", "Plaid API"],
      image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&h=600&fit=crop",
      duration: "10 months",
      teamSize: "10 developers",
      rating: 5,
      status: "Live",
      liveUrl: "https://example-fintech.com",
      completedDate: "2024-07-10"
    },
    {
      id: 4,
      title: "AI-Powered Analytics Dashboard",
      description: `Intelligent business analytics platform with machine learning insights, predictive modeling, and real-time data visualization.\n\nHelping businesses make data-driven decisions with 95% accuracy predictions.`,
      category: "Data Analytics",
      technologies: ["React", "Python", "TensorFlow", "D3.js", "AWS", "Elasticsearch"],
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&h=600&fit=crop",
      duration: "7 months",
      teamSize: "9 developers",
      rating: 4,
      status: "Live",
      completedDate: "2024-06-25"
    },
    {
      id: 5,
      title: "Smart IoT Home Automation",
      description: `Comprehensive IoT platform for smart home automation with voice control, energy monitoring, security integration, and mobile app control.\n\nConnecting 1000+ smart devices across residential and commercial properties.`,
      category: "IoT Solution",
      technologies: ["React", "Node.js", "MQTT", "InfluxDB", "Raspberry Pi", "Alexa Skills"],
      image: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=800&h=600&fit=crop",
      duration: "9 months",
      teamSize: "11 developers",
      rating: 5,
      status: "In Development",
      completedDate: "2024-05-15"
    },
    {
      id: 6,
      title: "Educational Learning Platform",
      description: `Interactive online learning platform with video streaming, progress tracking, gamification, and collaborative tools for students and educators.\n\nServing 25,000+ students across 100+ educational institutions.`,
      category: "EdTech",
      technologies: ["Next.js", "Node.js", "PostgreSQL", "WebRTC", "Stripe", "AWS"],
      image: "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=800&h=600&fit=crop",
      duration: "5 months",
      teamSize: "7 developers",
      rating: 4,
      status: "Live",
      liveUrl: "https://example-education.com",
      completedDate: "2024-04-30"
    }
  ];

  // Mock testimonials data
  const testimonials = [
    {
      id: 1,
      name: "Sarah Johnson",
      position: "CTO",
      company: "TechCorp Solutions",
      avatar: "https://randomuser.me/api/portraits/women/32.jpg",
      content: `Shutter Lifters transformed our vision into reality with their exceptional technical expertise and creative approach. The e-commerce platform they built exceeded all our expectations and delivered measurable results.`,
      projectType: "E-Commerce Platform",
      results: [
        { value: "300%", metric: "Sales Increase" },
        { value: "50%", metric: "Load Time Reduction" },
        { value: "99.9%", metric: "Uptime Achieved" }
      ]
    },
    {
      id: 2,
      name: "Michael Chen",
      position: "Founder & CEO",
      company: "HealthTech Innovations",
      avatar: "https://docpulse.com/wp-content/uploads/2024/02/slider-small-1.jpg",
      content: `The healthcare management system developed by Shutter Lifters has revolutionized how we serve our patients. Their attention to security, compliance, and user experience is unmatched in the industry.`,
      projectType: "Healthcare Management",
      results: [
        { value: "75%", metric: "Efficiency Gain" },
        { value: "50+", metric: "Healthcare Providers" },
        { value: "100%", metric: "HIPAA Compliance" }
      ]
    },
    {
      id: 3,
      name: "Emily Rodriguez",
      position: "Product Manager",
      company: "FinanceFlow Bank",
      avatar: "https://randomuser.me/api/portraits/women/28.jpg",
      content: `Working with Shutter Lifters on our mobile banking app was an incredible experience. They delivered a secure, user-friendly solution that our customers love and trust with their financial data.`,
      projectType: "Mobile Banking App",
      results: [
        { value: "$2M+", metric: "Daily Transactions" },
        { value: "4.8/5", metric: "App Store Rating" },
        { value: "Bank-Grade", metric: "Security Level" }
      ]
    }
  ];

  // Extract unique categories and technologies
  const categories = ['All', ...new Set(projects.map(p => p.category))];
  const technologies = ['All', ...new Set(projects.flatMap(p => p.technologies))];

  // Filter and sort projects
  const filteredProjects = useMemo(() => {
    let filtered = projects?.filter(project => {
      const matchesCategory = selectedCategory === 'All' || project?.category === selectedCategory;
      const matchesTechnology = selectedTechnology === 'All' || project?.technologies?.includes(selectedTechnology);
      const matchesSearch = !searchQuery ||
        project?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        project?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
        project?.technologies?.some(tech => tech?.toLowerCase()?.includes(searchQuery?.toLowerCase()));

      return matchesCategory && matchesTechnology && matchesSearch;
    });

    // Sort projects
    switch (sortBy) {
      case 'rating':
        filtered?.sort((a, b) => b?.rating - a?.rating);
        break;
      case 'name':
        filtered?.sort((a, b) => a?.title?.localeCompare(b?.title));
        break;
      case 'recent':
      default:
        filtered?.sort((a, b) => new Date(b.completedDate) - new Date(a.completedDate));
        break;
    }

    return filtered;
  }, [selectedCategory, selectedTechnology, searchQuery, sortBy]);

  const clearFilters = () => {
    setSelectedCategory('All');
    setSelectedTechnology('All');
    setSearchQuery('');
  };

  return (
    <>
      <Helmet>
        <title>Portfolio - Shutter Lifters | 3D Interactive Project Showcase</title>
        <meta name="description" content="Explore our portfolio of cutting-edge software projects featuring 3D interactive showcases, case studies, and client success stories. Discover how we transform ideas into digital reality." />
        <meta name="keywords" content="portfolio, projects, case studies, web development, mobile apps, software solutions, 3D showcase" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />

        {/* Hero Section */}
        <section className="relative pt-24 pb-16 overflow-hidden">
          {/* Background Elements */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-20 left-10 w-72 h-72 bg-accent/20 rounded-full blur-3xl" />
            <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent/10 rounded-full blur-3xl" />
          </div>

          <div className="container mx-auto px-6 lg:px-8 relative">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <div className="flex items-center justify-center space-x-3 mb-6">
                <div className="w-16 h-16 bg-accent/10 rounded-2xl flex items-center justify-center transform rotate-12 hover:rotate-45 transition-transform duration-500">
                  <Icon name="Briefcase" size={32} className="text-accent" />
                </div>
                <h1 className="text-4xl lg:text-6xl font-bold text-foreground">
                  Projects <span className="text-accent">Galaxy</span>
                </h1>
              </div>

              <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
                Explore our universe of innovative software solutions through immersive 3D showcases,\n
                detailed case studies, and client success stories that demonstrate our technical excellence.
              </p>

              <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
                <Button
                  variant="default"
                  size="lg"
                  iconName="Rocket"
                  iconPosition="left"
                  className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-accent"
                  onClick={() => document.getElementById('projects-section')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Explore Projects
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  iconName="Play"
                  iconPosition="left"
                  className="border-accent/30 text-accent hover:bg-accent"
                >
                  Watch Demo
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Project Stats */}
        <section className="py-16">
          <div className="container mx-auto px-6 lg:px-8">
            <ProjectStats stats={projectStats} />
          </div>
        </section>

        {/* Technology Stack Visualizer */}
        <section className="py-16">
          <div className="container mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                Technology <span className="text-accent">Ecosystem</span>
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Explore our comprehensive technology stack through an interactive 3D visualization
              </p>
            </motion.div>
            <TechStackVisualizer technologies={technologies} />
          </div>
        </section>

        {/* Projects Section */}
        <section id="projects-section" className="py-16">
          <div className="container mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                Featured <span className="text-accent">Projects</span>
              </h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Discover our portfolio of innovative solutions that have transformed businesses across industries
              </p>
            </motion.div>

            {/* Filter Bar */}
            <div className="mb-12">
              <FilterBar
                categories={categories}
                technologies={technologies}
                selectedCategory={selectedCategory}
                selectedTechnology={selectedTechnology}
                searchQuery={searchQuery}
                onCategoryChange={setSelectedCategory}
                onTechnologyChange={setSelectedTechnology}
                onSearchChange={setSearchQuery}
                onClearFilters={clearFilters}
                projectCount={filteredProjects?.length}
              />
            </div>

            {/* View Controls */}
            <div className="flex items-center justify-between mb-8">
              <div className="flex items-center space-x-4">
                <span className="text-sm text-muted-foreground">
                  Showing {filteredProjects?.length} of {projects?.length} projects
                </span>
              </div>

              <div className="flex items-center space-x-4">
                {/* Sort Options */}
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e?.target?.value)}
                  className="px-3 py-2 bg-card border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  <option value="recent">Most Recent</option>
                  <option value="rating">Highest Rated</option>
                  <option value="name">Alphabetical</option>
                </select>

                {/* View Mode Toggle */}
                <div className="flex items-center bg-muted/30 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:text-foreground'
                      }`}
                  >
                    <Icon name="Grid3X3" size={16} />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-md transition-colors ${viewMode === 'list' ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:text-foreground'
                      }`}
                  >
                    <Icon name="List" size={16} />
                  </button>
                </div>
              </div>
            </div>

            {/* Projects Grid */}
            <AnimatePresence mode="wait">
              {filteredProjects?.length > 0 ? (
                <motion.div
                  key="projects-grid"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className={`grid gap-8 ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'
                    }`}
                >
                  {filteredProjects?.map((project, index) => (
                    <ProjectCard
                      key={project?.id}
                      project={project}
                      index={index}
                    />
                  ))}
                </motion.div>
              ) : (
                <motion.div
                  key="no-projects"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="text-center py-16"
                >
                  <div className="w-24 h-24 bg-muted/30 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Icon name="Search" size={32} className="text-muted-foreground" />
                  </div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    No Projects Found
                  </h3>
                  <p className="text-muted-foreground mb-6">
                    Try adjusting your filters or search terms to find more projects.
                  </p>
                  <Button
                    variant="outline"
                    iconName="RotateCcw"
                    iconPosition="left"
                    onClick={clearFilters}
                    className="border-accent/30 text-accent hover:bg-accent/10"
                  >
                    Clear All Filters
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </section>

        {/* Client Testimonials */}
        <section className="py-16">
          <div className="container mx-auto px-6 lg:px-8">
            <ClientTestimonials testimonials={testimonials} />
          </div>
        </section>

        {/* Call to Action */}
        <section className="py-16">
          <div className="container mx-auto px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-card rounded-2xl p-8 lg:p-12 text-center shadow-3d border border-border/50 relative overflow-hidden"
            >
              {/* Background Pattern */}
              <div className="absolute inset-0 opacity-5">
                <div className="w-full h-full" style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M20 20c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10zm10 0c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10z'/%3E%3C/g%3E%3C/svg%3E")`,
                }} />
              </div>

              <div className="relative">
                <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                  Ready to Start Your <span className="text-accent">Next Project</span>?
                </h2>
                <p className="text-muted-foreground text-lg mb-8 max-w-2xl mx-auto">
                  Let's transform your ideas into innovative digital solutions that drive results and exceed expectations.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
                  <Button
                    variant="default"
                    size="lg"
                    iconName="MessageCircle"
                    iconPosition="left"
                    // routing to contact
                    onClick={() => navigate("/contact")}
                    className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-accent"
                  >
                    Start a Conversation
                  </Button>
                  {/* inserting calendly */}

                    <CalendlyWidget
                      buttonText='Schedule Consultation'
                    buttonClassName='inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-11 rounded-md px-8 hover:text-accent-foreground hover:bg-accent shadow-accent'
                    />


                  {/* <Button
                    variant="outline"
                    size="lg"
                    iconName="Calendar"
                    iconPosition="left"
                    className="border-accent/30 text-accent hover:bg-accent"
                  >
                    Schedule Consultation
                  </Button> */}
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Portfolio;